import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Marketing-site analytics: page views, events, and signup attribution.
 *
 * Why a custom dataLayer-push layer instead of react-ga4 / similar?
 * - The legacy GTM container (GTM-PVGMLS5S) is already configured to
 *   forward to GA4 (G-C0W50D4MCX), Hotjar, etc. — adding a second SDK
 *   would double-count events. We just feed dataLayer.
 * - SPAs need explicit page_view pushes on route change because GTM's
 *   automatic pageview only fires on full document loads.
 *
 * UTM attribution captures the first-touch landing context (UTM
 * params, gclid/fbclid, referrer, full URL) into sessionStorage so
 * the get-started flow can read it back at signup time and POST it
 * to /api/v1/auth/register as `signupSource`. SessionStorage scopes
 * to the tab so multi-source split-tests don't bleed across windows.
 */

const SIGNUP_SOURCE_STORAGE_KEY = "uniflo.signupSource";

const TRACKED_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
  "source_type",
  "source_slug",
  "cta_id",
  "comparison",
  "use_case",
  "template",
  "cohesion",
  "pricing_plan",
];

function getDataLayer() {
  if (typeof window === "undefined") return null;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = window.dataLayer || [];
  }
  return window.dataLayer;
}

/**
 * Push an event to GTM's dataLayer. Safe to call before GTM loads —
 * GTM picks up queued events when it boots.
 */
export function trackEvent(eventName, params = {}) {
  const dataLayer = getDataLayer();
  if (!dataLayer || !eventName) return;
  dataLayer.push({ event: eventName, ...params });
}

/**
 * Fire a page_view on every React Router location change. The first
 * page load is auto-captured by GTM's bootstrap; subsequent SPA
 * navigations rely on this hook.
 */
export function usePageViewTracking() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = `${location.pathname}${location.search}`;
    trackEvent("page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
}

function readStoredSignupSource() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SIGNUP_SOURCE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeStoredSignupSource(source) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      SIGNUP_SOURCE_STORAGE_KEY,
      JSON.stringify(source)
    );
  } catch (_) {
    // sessionStorage may be unavailable (privacy mode) — degrade silently.
  }
}

function buildSourceFromCurrentLocation() {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const captured = {};
  let hasAny = false;

  for (const key of TRACKED_QUERY_KEYS) {
    const value = params.get(key);
    if (value) {
      captured[key] = value;
      hasAny = true;
    }
  }

  // Always record landing context — even with no UTMs, the referrer
  // and entry URL are useful for funnel attribution.
  return {
    ...captured,
    referrer: document.referrer || "",
    landingPage: `${url.pathname}${url.search}`,
    landingHost: url.host,
    firstTouchAt: new Date().toISOString(),
    hasMarketingTags: hasAny,
  };
}

/**
 * Capture UTM / referrer / landing-page context the first time a
 * visitor lands. First-touch wins: subsequent navigations within the
 * same tab don't overwrite the original source. This is the "what
 * marketing campaign brought this user in" signal that gets persisted
 * to the User record at signup time.
 */
export function useUtmCapture() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const existing = readStoredSignupSource();
    if (existing) return; // first-touch wins

    const source = buildSourceFromCurrentLocation();
    if (source) {
      writeStoredSignupSource(source);
    }
  }, []);
}

/**
 * Read the captured signup source. Used by the get-started signup
 * flow to forward attribution into the /api/v1/auth/register payload.
 */
export function getStoredSignupSource() {
  return readStoredSignupSource();
}

/**
 * Clear the stored signup source. Called after a successful signup
 * so subsequent visits in the same tab capture fresh attribution.
 */
export function clearStoredSignupSource() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SIGNUP_SOURCE_STORAGE_KEY);
  } catch (_) {
    // ignore
  }
}

/**
 * Single document-level click listener that converts every click
 * inside an element annotated with `data-track="<cta_id>"` into a
 * `cta_click` dataLayer event. Annotating CTAs declaratively keeps
 * page components free of inline analytics plumbing — to instrument
 * a button or link, just add the data-track attribute.
 *
 * Conventions:
 *   data-track             — required cta_id (e.g. "hero_primary")
 *   data-track-location    — optional section/page identifier
 *                            (e.g. "landing_hero", "case_study_nihss")
 *   data-track-plan        — optional plan slug, surfaced for pricing
 *                            CTAs so revenue attribution is per-plan
 *   data-track-destination — optional override for the destination
 *                            URL when the CTA isn't a real <a>/<Link>
 *
 * Walks up from event.target so clicks on icons / spans inside a
 * tracked button still fire correctly.
 */
export function useCtaTracking() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    function handleClick(event) {
      let element = event.target;
      while (element && element !== document.body) {
        if (element.dataset && element.dataset.track) {
          const ctaId = `${element.dataset.track || ""}`.trim();
          if (!ctaId) return;
          const location = `${element.dataset.trackLocation || ""}`.trim();
          const plan = `${element.dataset.trackPlan || ""}`.trim();
          const destination =
            `${
              element.dataset.trackDestination ||
              element.getAttribute?.("href") ||
              ""
            }`.trim();
          const params = { cta_id: ctaId };
          if (location) params.location = location;
          if (plan) params.plan = plan;
          if (destination) params.destination = destination;
          trackEvent("cta_click", params);
          return;
        }
        element = element.parentElement;
      }
    }

    document.addEventListener("click", handleClick, { capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, []);
}
